import {Section} from '@github-ui/issue-metadata/Section'
import {SectionHeader} from '@github-ui/issue-metadata/SectionHeader'
import {IssueStatusIcon} from '@github-ui/issue-pr-state/IssueStatusIcon'
import {SharedPicker} from '@github-ui/item-picker-base/SharedPicker'
import {IssueOpenedIcon} from '@primer/octicons-react'
import {ActionList, ActionMenu} from '@primer/react'
import {type RefObject, useRef, useState} from 'react'

import type {IssueCreateStatus} from '../hooks/use-issue-form-secondary-query'
import styles from './IssueStatusPicker.module.css'

type IssueStatusPickerProps = {
  statuses: readonly IssueCreateStatus[]
  status: IssueCreateStatus | null
  onSelectionChange: (status: IssueCreateStatus | null) => void
}

function IssueStatusMenu({
  statuses,
  status,
  onSelectionChange,
  anchorRef,
  open,
  setOpen,
}: IssueStatusPickerProps & {
  anchorRef: RefObject<HTMLButtonElement | null>
  open: boolean
  setOpen: (open: boolean) => void
}) {
  return (
    <ActionMenu anchorRef={anchorRef} open={open} onOpenChange={setOpen}>
      <ActionMenu.Overlay width="small">
        <ActionList selectionVariant="single" showDividers={false} aria-label="Status">
          {statuses.map(option => (
            <ActionList.Item
              key={option.id}
              selected={option.id === status?.id}
              onSelect={() => onSelectionChange(option)}
            >
              <ActionList.LeadingVisual>
                <IssueStatusIcon status={option} />
              </ActionList.LeadingVisual>
              {option.name}
            </ActionList.Item>
          ))}
        </ActionList>
      </ActionMenu.Overlay>
    </ActionMenu>
  )
}

/** The full-page form's sidebar section. */
export function CreateIssueStatusSection(props: IssueStatusPickerProps) {
  const anchorRef = useRef<HTMLButtonElement>(null)
  const [open, setOpen] = useState(false)
  const {status} = props

  return (
    <>
      <Section
        id="sidebar-status-section"
        sectionHeader={
          <SectionHeader
            title="Status"
            ref={anchorRef}
            buttonProps={{onClick: () => setOpen(!open), 'aria-haspopup': 'true', 'aria-expanded': open}}
          />
        }
        emptyText={status ? undefined : 'No status'}
      >
        {status && (
          <div className={styles.selectedStatus}>
            <IssueStatusIcon status={status} />
            <span>{status.name}</span>
          </div>
        )}
      </Section>
      <IssueStatusMenu {...props} anchorRef={anchorRef} open={open} setOpen={setOpen} />
    </>
  )
}

/** The dialog's footer button. */
export function CreateIssueStatusPicker(props: IssueStatusPickerProps) {
  const anchorRef = useRef<HTMLButtonElement>(null)
  const [open, setOpen] = useState(false)
  const {status} = props

  return (
    <>
      <SharedPicker
        ref={anchorRef}
        anchorText="Status"
        ariaLabel="Select status"
        sharedPickerMainValue={status?.name}
        leadingIcon={IssueOpenedIcon}
        leadingIconElement={status && <IssueStatusIcon status={status} />}
        anchorProps={{onClick: () => setOpen(!open), 'aria-haspopup': 'true', 'aria-expanded': open}}
      />
      <IssueStatusMenu {...props} anchorRef={anchorRef} open={open} setOpen={setOpen} />
    </>
  )
}
